import { useMemo, useRef, useState, useLayoutEffect } from "react";
import { sankey } from "d3-sankey";
import Sankey from "./Sankey";
import { fontSizes } from "../../typography";
import sankeyData from "../../../data/sankey.json";
import type {
  SankeyNodeData,
  SankeyData,
  SankeyNode,
  SankeyLink,
  SankeyGraph,
} from "./types";
import { dataVizClassificationPalette } from "@atlas-int/tokens";

const data: SankeyData = sankeyData as SankeyData;
const COLOR_PALETTE = dataVizClassificationPalette;
const LEVEL_LABELS: Record<number, string> = {
  0: "HS2007",
  1: "HS2002",
  2: "HS1997",
  3: "HS1992",
};

const getColorByDepth = (depth: number): string => {
  return COLOR_PALETTE[depth % COLOR_PALETTE.length];
};

type BBox = { x: number; y: number; width: number; height: number };

interface AnimatedSankeyProps {
  svgWidth: number;
  svgHeight: number;
  currentStep: string;
  isNarrow?: boolean;
}

const ROUTE_WEIGHTS = {
  "854231->854221->854213->854211": 0.54,
  "854231->854221->854219->854219": 0.22,
  "854231->854260->854240->854220": 0.09,
  "854231->854221->854214->854211": 0.07,
  "854231->854221->854219->854211": 0.07,
  "854231->854229->854230->854219": 0.01,
  "854231->854890->854890->854800": 0,
};

const ROUTE_UN_WEIGHTS = {
  "854231->854221->854213->854211": 0,
  "854231->854221->854219->854219": 0,
  "854231->854260->854240->854220": 0,
  "854231->854221->854214->854211": 0,
  "854231->854221->854219->854211": 0,
  "854231->854229->854230->854219": 1,
  "854231->854890->854890->854800": 0,
};

// Helper to measure bbox safely
const measureBBox = (element: SVGElement | null): BBox | null => {
  if (!element) return null;
  try {
    const bbox = (element as SVGGraphicsElement).getBBox();
    return { x: bbox.x, y: bbox.y, width: bbox.width, height: bbox.height };
  } catch {
    return null;
  }
};

export default function Figure4({
  svgWidth,
  svgHeight,
  currentStep,
  isNarrow,
}: AnimatedSankeyProps & { isNarrow: boolean }) {
  const [depthCenters, setDepthCenters] = useState<
    Array<{ depth: number; x: number }>
  >([]);
  const [titleBbox, setTitleBbox] = useState<BBox | null>(null);
  const [levelLabelBboxes, setLevelLabelBboxes] = useState<Map<number, BBox>>(
    new Map(),
  );

  const titleTextRef = useRef<SVGTextElement>(null);
  const levelLabelRefs = useRef<Map<number, SVGTextElement>>(new Map());

  // Layout dimensions
  const sankeyHeight = isNarrow ? svgHeight / 2.65 : svgHeight / 3;
  const titleSpacing = isNarrow ? 32 : 40;
  const gapAboveDivider = 50;
  const gapBelowDivider = 50;

  // Scaling for narrow viewports
  const refWidth = 500;
  const uniformScale = svgWidth < refWidth ? svgWidth / refWidth : 1;
  const layoutWidth = svgWidth < refWidth ? refWidth : svgWidth;
  const innerWidth = layoutWidth;

  const sectionHeight = sankeyHeight * 1.1;
  const sectionBlockHeight = titleSpacing + sectionHeight;

  const sankeySections = useMemo(
    () => [
      {
        title: "Comtrade Mapping of Products",
        weights: ROUTE_UN_WEIGHTS,
      },
      {
        title: "Growth Lab Weighted Mapping of Products",
        weights: ROUTE_WEIGHTS,
        hasTopBorder: true,
      },
    ],
    [],
  );

  // Measure title bbox
  useLayoutEffect(() => {
    const bbox = measureBBox(titleTextRef.current);
    if (bbox) setTitleBbox(bbox);
  }, [data, svgWidth, svgHeight]);

  // Measure level label bboxes
  useLayoutEffect(() => {
    const bboxes = new Map<number, BBox>();
    levelLabelRefs.current.forEach((element, depth) => {
      const bbox = measureBBox(element);
      if (bbox) bboxes.set(depth, bbox);
    });
    setLevelLabelBboxes(bboxes);
  }, [data, svgWidth, svgHeight, depthCenters]);

  // Compute sankey layout (shared by both sections)
  const graph = useMemo((): SankeyGraph => {
    const layoutHeight = sankeyHeight;
    const NODE_HEIGHT = layoutHeight / 14;
    const NODE_PADDING = layoutHeight / 12;
    const END_NODE_PADDING = layoutHeight / 9;

    const generator = sankey()
      .nodeId((d: { id: number }) => d.id)
      .nodeWidth(20)
      .nodePadding(NODE_PADDING)
      .extent([
        [80, 1],
        [innerWidth - 170, layoutHeight - 6],
      ]);

    const g = generator({ nodes: data.nodes, links: data.links });

    // Map original names
    const nodeMap = new Map(data.nodes.map((n: SankeyNodeData) => [n.id, n]));
    g.nodes.forEach((node) => {
      const typedNode = node as SankeyNode;
      const originalNode = nodeMap.get(typedNode.id);
      if (originalNode) {
        typedNode.name = originalNode.name;
      }
    });

    // Center nodes vertically per depth with uniform height
    if (g.nodes.length > 0) {
      const nodesByDepth: SankeyNode[][] = [];
      g.nodes.forEach((node) => {
        const typedNode = node as SankeyNode;
        if (!nodesByDepth[typedNode.depth]) nodesByDepth[typedNode.depth] = [];
        nodesByDepth[typedNode.depth].push(typedNode);
      });

      const maxDepth = Math.max(...g.nodes.map((n) => (n as SankeyNode).depth));

      nodesByDepth.forEach((nodesInColumn: SankeyNode[], depth: number) => {
        const padding = depth === maxDepth ? END_NODE_PADDING : NODE_PADDING;
        const columnHeight =
          nodesInColumn.length * NODE_HEIGHT +
          (nodesInColumn.length - 1) * padding;
        const topPadding = (layoutHeight - columnHeight) / 2;
        nodesInColumn.forEach((node: SankeyNode, i: number) => {
          node.y0 = topPadding + i * (NODE_HEIGHT + padding);
          node.y1 = node.y0 + NODE_HEIGHT;
        });
      });

      g.links.forEach((link) => {
        const typedLink = link as SankeyLink;
        typedLink.y0 =
          typedLink.source.y0 + (typedLink.source.y1 - typedLink.source.y0) / 2;
        typedLink.y1 =
          typedLink.target.y0 + (typedLink.target.y1 - typedLink.target.y0) / 2;
      });
    }

    return g as SankeyGraph;
  }, [innerWidth, sankeyHeight]);

  const totalHeight =
    sectionBlockHeight * sankeySections.length +
    gapAboveDivider +
    gapBelowDivider;

  const scaledWidth = layoutWidth;
  const scaledHeight = svgHeight / uniformScale;

  const fitScale = totalHeight > scaledHeight ? scaledHeight / totalHeight : 1;
  const topMargin = Math.max(0, (scaledHeight - totalHeight * fitScale) / 2);

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        height: "100%",
      }}
    >
      <svg
        width="100%"
        height="90%"
        viewBox={`0 0 ${scaledWidth} ${scaledHeight}`}
        style={{
          transform: `scale(${uniformScale})`,
          transformOrigin: "top center",
        }}
      >
        <g transform={`translate(0, ${topMargin}) scale(1, ${fitScale})`}>
          {sankeySections.map(({ title, weights, hasTopBorder }, idx) => {
            const offsetY =
              idx === 0 ? 0 : sectionBlockHeight + gapAboveDivider;
            const headerYOffset = hasTopBorder ? 10 : 0;
            const topTitleOffset = idx === 0 && !isNarrow ? 16 : 0;

            return (
              <g key={title} transform={`translate(0, ${offsetY})`}>
                {hasTopBorder && (
                  <line
                    x1={0}
                    x2={innerWidth}
                    y1={0}
                    y2={0}
                    stroke="lightgray"
                    strokeWidth={1}
                  />
                )}
                {hasTopBorder && depthCenters.length > 0 && (
                  <g>
                    {depthCenters.map(({ depth, x }) => {
                      const label = LEVEL_LABELS[depth];
                      if (!label) return null;

                      const nodesAtDepth = graph.nodes.filter(
                        (n: SankeyNode) => n.depth === depth,
                      );
                      const nodeY0Values = nodesAtDepth
                        .map((n: SankeyNode) => n.y0)
                        .filter((y: number) => Number.isFinite(y));
                      const nodeY1Values = nodesAtDepth
                        .map((n: SankeyNode) => n.y1)
                        .filter((y: number) => Number.isFinite(y));

                      const minY0 =
                        nodeY0Values.length > 0 ? Math.min(...nodeY0Values) : 0;
                      const maxY1 =
                        nodeY1Values.length > 0
                          ? Math.max(...nodeY1Values)
                          : null;
                      const nodeHeight =
                        nodesAtDepth.length > 0
                          ? nodesAtDepth[0].y1 - nodesAtDepth[0].y0
                          : 0;

                      const parsedFontSize = parseFloat(
                        fontSizes.figure4LevelLabels,
                      );
                      const safeFontSize = Number.isFinite(parsedFontSize)
                        ? parsedFontSize
                        : 16;
                      const textHalfHeight = (safeFontSize * 1.2) / 2;
                      const pctLabelOffset =
                        depth === 1 || depth === 2 ? (isNarrow ? 14 : 16) : 0;

                      const upTarget =
                        maxY1 !== null
                          ? maxY1 +
                            nodeHeight +
                            pctLabelOffset -
                            (sectionHeight + gapAboveDivider)
                          : null;
                      const downTarget = minY0 + titleSpacing + gapBelowDivider;
                      const bbox = levelLabelBboxes.get(depth);

                      return (
                        <g key={`divider-label-group-${depth}`}>
                          {bbox && (
                            <rect
                              x={bbox.x - 6}
                              y={bbox.y - 3}
                              width={bbox.width + 12}
                              height={bbox.height + 6}
                              fill="white"
                              className="whiteboxes"
                              style={{ pointerEvents: "none" }}
                            />
                          )}
                          {upTarget !== null && Number.isFinite(upTarget) && (
                            <line
                              x1={x}
                              y1={-textHalfHeight}
                              x2={x}
                              y2={upTarget}
                              stroke="#2C2C2C"
                              strokeWidth={1}
                              strokeDasharray="4,4"
                              opacity={0.6}
                              style={{ pointerEvents: "none" }}
                            />
                          )}
                          {Number.isFinite(downTarget) && (
                            <line
                              x1={x}
                              y1={textHalfHeight}
                              x2={x}
                              y2={downTarget}
                              stroke="#2C2C2C"
                              strokeWidth={1}
                              strokeDasharray="4,4"
                              opacity={0.6}
                              style={{ pointerEvents: "none" }}
                            />
                          )}
                          <text
                            ref={(el) => {
                              if (el) levelLabelRefs.current.set(depth, el);
                            }}
                            x={x}
                            y={0}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fontSize={fontSizes.figure4LevelLabels}
                            fontWeight={600}
                            fill={getColorByDepth(depth)}
                            style={{ pointerEvents: "none" }}
                          >
                            {label}
                          </text>
                        </g>
                      );
                    })}
                  </g>
                )}
                <g
                  transform={
                    hasTopBorder
                      ? `translate(0, ${gapBelowDivider})`
                      : undefined
                  }
                >
                  {hasTopBorder && titleBbox && (
                    <rect
                      x={titleBbox.x - 8}
                      y={titleBbox.y - 6}
                      width={titleBbox.width + 16}
                      height={titleBbox.height + 12}
                      fill="white"
                      style={{ pointerEvents: "none" }}
                      className="whitebox"
                    />
                  )}
                  <text
                    ref={titleTextRef}
                    x={innerWidth / 2}
                    y={titleSpacing / 2 + headerYOffset + topTitleOffset}
                    textAnchor="middle"
                    fontWeight={600}
                    fill="rgb(77,77,77)"
                    style={{ textTransform: "uppercase" }}
                    fontSize={fontSizes.vizTitle1}
                  >
                    {title}
                  </text>

                  <Sankey
                    graph={graph}
                    width={innerWidth}
                    height={sectionHeight}
                    diagramHeight={sankeyHeight}
                    currentStepIndex={currentStep}
                    routeWeights={weights}
                    isNarrow={isNarrow}
                    embed
                    translateY={titleSpacing}
                    onDepthCenters={hasTopBorder ? setDepthCenters : undefined}
                  />
                </g>
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
}
